/**
 * useContactTimeline — 1 contact の audience_events を時系列で取得。
 *
 *  - contactId / lineUid 両方でクエリして同一人物のイベントを統合
 *  - dedupeKey で重複イベントを除外（contactId と lineUid 両方持ちの場合の二重ヒット対策）
 *  - timestamp 降順、デフォルト 100 件
 *  - targetId を mails / line_news / forms / landings から逆引きして表示用ラベル化
 *
 * Firestore index: (contactId, timestamp DESC), (lineUid, timestamp DESC) — 既存 ✅
 */
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit as limitFn,
  orderBy,
  query,
  where,
  type Timestamp,
} from 'firebase/firestore';

import type {
  AudienceEvent,
  AudienceEventSource,
  AudienceEventType,
  AudienceTargetType,
} from '@goenhub/shared';

import { db } from '@/lib/firebase';

export interface ContactTimelineEvent {
  id: string;
  type: AudienceEventType;
  source: AudienceEventSource;
  campaignId: string | null;
  targetId: string;
  targetType: AudienceTargetType;
  /** 逆引きしたターゲット名（subject / title / name 等）。失敗時は null */
  targetLabel: string | null;
  /** visitor ID（localStorage 由来）。匿名 → 顕名 backfill されたか判別に使う */
  visitorId: string | null;
  metadata: Record<string, unknown>;
  timestamp: Timestamp | null;
}

const TARGET_COLLECTION: Record<AudienceTargetType, string> = {
  mail_schedule: 'mail_schedules',
  line_news: 'line_news',
  form: 'forms',
  landing: 'landings',
  document: 'documents',
};

/**
 * targetId をコレクションごとにバッチ逆引き。
 * mail_schedule は subject、line_news は title、form は name、landing は title を取る。
 */
async function resolveTargetLabels(
  pairs: Array<{ targetType: AudienceTargetType; targetId: string }>,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  // (col, id) でユニーク化
  const byCol = new Map<string, Set<string>>();
  for (const p of pairs) {
    const col = TARGET_COLLECTION[p.targetType];
    if (!col || !p.targetId) continue;
    const set = byCol.get(col) ?? new Set<string>();
    set.add(p.targetId);
    byCol.set(col, set);
  }
  // documentId() で「in 10 件まで」のクエリで一括取得
  await Promise.all(
    Array.from(byCol.entries()).flatMap(async ([col, idSet]) => {
      const ids = Array.from(idSet);
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));
      await Promise.all(
        chunks.map(async (chunk) => {
          try {
            const snap = await getDocs(
              query(collection(db, col), where(documentId(), 'in', chunk)),
            );
            for (const d of snap.docs) {
              const data = d.data() as {
                subject?: string;
                title?: string;
                name?: string;
              };
              const label =
                data.subject ?? data.title ?? data.name ?? null;
              if (label) result.set(`${col}:${d.id}`, label);
            }
          } catch {
            // 個別取得にフォールバック
            for (const id of chunk) {
              try {
                const snap = await getDoc(doc(db, col, id));
                if (snap.exists()) {
                  const data = snap.data() as {
                    subject?: string;
                    title?: string;
                    name?: string;
                  };
                  const label = data.subject ?? data.title ?? data.name ?? null;
                  if (label) result.set(`${col}:${id}`, label);
                }
              } catch {
                // ignore
              }
            }
          }
        }),
      );
    }),
  );
  return result;
}

interface UseContactTimelineArgs {
  contactId: string | null | undefined;
  lineUid?: string | null;
  limit?: number;
  /** クエリを無効化（パネル閉じ等で fetch 抑制） */
  enabled?: boolean;
}

export function useContactTimeline({
  contactId,
  lineUid,
  limit = 100,
  enabled = true,
}: UseContactTimelineArgs) {
  return useQuery({
    queryKey: ['contact_timeline', contactId, lineUid, limit],
    enabled: enabled && !!contactId,
    staleTime: 30_000,
    queryFn: async (): Promise<ContactTimelineEvent[]> => {
      if (!contactId) return [];

      // contactId クエリ（必須）
      const contactSnap = await getDocs(
        query(
          collection(db, 'audience_events'),
          where('contactId', '==', contactId),
          orderBy('timestamp', 'desc'),
          limitFn(limit),
        ),
      );
      // lineUid クエリ（任意、同一人物の LINE 由来イベントも統合）
      let lineSnap = null as Awaited<ReturnType<typeof getDocs>> | null;
      if (lineUid) {
        try {
          lineSnap = await getDocs(
            query(
              collection(db, 'audience_events'),
              where('lineUid', '==', lineUid),
              orderBy('timestamp', 'desc'),
              limitFn(limit),
            ),
          );
        } catch {
          lineSnap = null;
        }
      }

      // マージ（doc.id ベースで重複除去：contactId/lineUid 両方ヒット時の二重取り対策）
      const seen = new Set<string>();
      const merged: ContactTimelineEvent[] = [];
      const ingest = (id: string, raw: unknown) => {
        if (seen.has(id)) return;
        seen.add(id);
        const data = raw as Partial<AudienceEvent> & {
          timestamp?: Timestamp;
        };
        if (!data.type || !data.targetId || !data.targetType) return;
        merged.push({
          id,
          type: data.type,
          source: data.source ?? 'direct',
          campaignId: data.campaignId ?? null,
          targetId: data.targetId,
          targetType: data.targetType,
          targetLabel: null, // 後で逆引きして埋める
          visitorId: data.visitorId ?? null,
          metadata: data.metadata ?? {},
          timestamp: data.timestamp ?? null,
        });
      };
      for (const d of contactSnap.docs) ingest(d.id, d.data());
      if (lineSnap) for (const d of lineSnap.docs) ingest(d.id, d.data());

      // timestamp 降順
      merged.sort((a, b) => {
        const ta = a.timestamp?.toMillis?.() ?? 0;
        const tb = b.timestamp?.toMillis?.() ?? 0;
        return tb - ta;
      });
      const trimmed = merged.slice(0, limit);

      // ターゲット名逆引き
      const labelMap = await resolveTargetLabels(
        trimmed.map((e) => ({ targetType: e.targetType, targetId: e.targetId })),
      );
      for (const ev of trimmed) {
        const col = TARGET_COLLECTION[ev.targetType];
        ev.targetLabel = labelMap.get(`${col}:${ev.targetId}`) ?? null;
      }
      return trimmed;
    },
  });
}
